<script lang="ts" setup>
import { getResolvedRefDeep } from '@ahmttyydn/api-client/v2/blocks/operation-code-sample'
import { ScalarCodeBlock, ScalarVirtualText } from '@ahmttyydn/components'
import { prettyPrintJson } from '@ahmttyydn/helpers/json/pretty-print-json'
import { getExampleFromSchema } from '@ahmttyydn/workspace-store/request-example'
import type {
  ExampleObject,
  MediaTypeObject,
  SchemaObject,
} from '@ahmttyydn/workspace-store/schemas/v3.1/strict/openapi-document'
import { computed } from 'vue'

const { example, response } = defineProps<{
  response: MediaTypeObject | undefined
  example: ExampleObject | undefined
}>()

/** Get content from the appropriate source */
const getContent = () => {
  if (example !== undefined) {
    return getResolvedRefDeep(example)?.value ?? ''
  }

  if (response?.schema) {
    return getExampleFromSchema(
      // Should be safe to deep resolve the schema here because we don't have to do any sibling resolution for example generation
      getResolvedRefDeep(response.schema) as SchemaObject,
      {
        emptyString: 'string',
        mode: 'read',
      },
    )
  }

  return undefined
}

/** Pre-pretty printed content string, avoids multiple pretty prints*/
const prettyPrintedContent = computed(() => {
  const content = getContent()
  if (content === undefined) {
    return undefined
  }
  return prettyPrintJson(content)
})

const VIRTUALIZATION_THRESHOLD = 20_000

// Virtualize the code block if it's too large
const shouldVirtualize = computed(() => {
  if (prettyPrintedContent.value === undefined) {
    return false
  }
  return prettyPrintedContent.value.length > VIRTUALIZATION_THRESHOLD
})
</script>
<template>
  <!-- Example -->
  <ScalarCodeBlock
    v-if="prettyPrintedContent !== undefined && !shouldVirtualize"
    class="bg-b-2"
    lang="json"
    :prettyPrintedContent="prettyPrintedContent" />

  <ScalarVirtualText
    v-else-if="prettyPrintedContent !== undefined && shouldVirtualize"
    containerClass="custom-scroll scalar-code-block border rounded-b flex flex-1 max-h-screen"
    contentClass="language-plaintext whitespace-pre font-code text-base"
    :lineHeight="20"
    :text="prettyPrintedContent" />

  <div
    v-else
    class="empty-state">
    No Body
  </div>
</template>

<style scoped>
.empty-state {
  margin: 10px 0 10px 12px;
  text-align: center;
  font-size: var(--scalar-mini);
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--scalar-radius-lg);
  color: var(--scalar-color-2);
}

.rule-title {
  font-family: var(--scalar-font-code);
  color: var(--scalar-color-1);
  display: inline-block;
  margin: 12px 0 6px;
  border-radius: var(--scalar-radius);
}

.rule {
  margin: 0 12px 0;
  border-radius: var(--scalar-radius-lg);
}

.rule-items {
  counter-reset: list-number;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 1px solid var(--scalar-border-color);
  padding: 12px 0 12px;
}
.rule-item {
  counter-increment: list-number;
  border: 1px solid var(--scalar-border-color);
  border-radius: var(--scalar-radius-lg);
  overflow: hidden;
  margin-left: 24px;
}
.rule-item:before {
  /* content: counter(list-number); */
  border: 1px solid var(--scalar-border-color);
  border-top: 0;
  border-right: 0;
  content: ' ';
  display: block;
  width: 24px;
  height: 6px;
  border-radius: 0 0 0 var(--scalar-radius-lg);
  margin-top: 6px;
  color: var(--scalar-color-2);
  transform: translateX(-25px);
  color: var(--scalar-color-1);
  position: absolute;
}
</style>
